import { Outlet, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const BG_IMG =
  'https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=2400&q=80';
// (vue large d'une voiture sport sur route ouverte, ambiance sombre cinématique)

// Tunnel v2 « plaque d'abord » — flux 23/07/26 : coordonnées AVANT le prix.
const STEPS = [
  { path: '/estimation', label: 'Plaque' },
  { path: '/estimation/coordonnees', label: 'Coordonnées' },
  { path: '/estimation/analyse', label: 'Analyse' },
  { path: '/estimation/resultat', label: 'Résultat' },
  { path: '/estimation/rdv', label: 'Rendez-vous' },
];

export default function EstimationLayout() {
  const { pathname } = useLocation();
  // Match the longest path prefix so /estimation/analyse, /rdv/confirmation, etc. resolve correctly
  const stepIndex = (() => {
    // Legacy : les liens /rdv?lead=... des emails déjà envoyés → étape Rendez-vous.
    if (pathname === '/rdv' || pathname.startsWith('/rdv/')) return STEPS.length - 1;
    const sorted = [...STEPS].map((s, i) => ({ ...s, i })).sort((a, b) => b.path.length - a.path.length);
    const hit = sorted.find(s => pathname === s.path || pathname.startsWith(s.path + '/'));
    return hit ? hit.i : -1;
  })();
  const isTunnel = stepIndex >= 0;
  const progress = isTunnel ? ((stepIndex + 1) / STEPS.length) * 100 : 0;

  return (
    <div className="relative min-h-screen overflow-hidden bg-primary-bg text-white">
      {/* Background image with parallax-ish blur */}
      <div className="fixed inset-0 -z-10">
        <img src={BG_IMG} alt="" className="h-full w-full object-cover object-center opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary-bg/80 via-primary-bg/70 to-primary-surface/90" />
        <div className="absolute inset-0 bg-radial-gold opacity-50" />
      </div>

      {isTunnel && (
        <header className="relative px-4 md:px-16 py-6">
          <div className="mx-auto flex max-w-5xl items-center justify-between">
            <Link to="/" aria-label="Stella Cars" className="flex items-center text-white">
              <img src="/logo.png" alt="Stella Cars" className="h-10 w-auto" />
            </Link>
            <Link to="/" className="text-xs uppercase tracking-[0.2em] text-slate-400 hover:text-accent-gold transition">
              Quitter
            </Link>
          </div>

          {/* Progress bar — desktop labels, mobile compact (only active visible) */}
          <div className="mx-auto mt-8 max-w-3xl">
            <div className="hidden sm:flex justify-between text-[11px] uppercase tracking-[0.18em] text-slate-400">
              {STEPS.map((s, i) => (
                <span
                  key={s.path}
                  className={`transition-colors ${
                    i === stepIndex
                      ? 'text-accent-gold font-semibold'
                      : i < stepIndex
                        ? 'text-accent-gold/70'
                        : ''
                  }`}
                >
                  {String(i + 1).padStart(2, '0')} · {s.label}
                </span>
              ))}
            </div>
            {/* Mobile: only current step label centered */}
            <div className="sm:hidden flex items-center justify-between text-[11px] uppercase tracking-[0.18em] text-slate-400">
              <span>Étape {stepIndex + 1}/{STEPS.length}</span>
              <span className="text-accent-gold font-semibold">{STEPS[stepIndex].label}</span>
            </div>
            <div className="mt-3 h-[2px] w-full overflow-hidden rounded-full bg-white/5">
              <motion.div
                initial={false}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="h-full bg-gradient-to-r from-accent-gold via-accent-goldLight to-accent-gold"
              />
            </div>
          </div>
        </header>
      )}

      <main className={`relative flex flex-1 items-start justify-center ${isTunnel ? 'px-4 py-6 md:py-12' : ''}`}>
        <AnimatePresence mode="wait">
          <motion.div
            key={pathname}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className={isTunnel ? 'w-full max-w-3xl' : 'w-full'}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>

      {isTunnel && (
        <footer className="relative px-4 py-8 text-center text-xs text-slate-500">
          © {new Date().getFullYear()} Stella Cars · Vos données sont protégées
        </footer>
      )}
    </div>
  );
}
